import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/** @jsx jsx */
import { useCallback, useState } from 'react';
import { css, jsx } from '@emotion/react';
import Button from '@atlaskit/button/custom-theme-button';
import InlineDialog from '@atlaskit/inline-dialog';
import * as colors from '@atlaskit/theme/colors';
import { themed, useGlobalTheme } from '@atlaskit/theme/components';
import { VAR_SECONDARY_TEXT_COLOR } from '../../constants';
import MessageIcon from '../message-icon';
var buttonContentsStyles = css({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none'
});
var titleStyles = css({
  padding: "var(--ds-space-0, 0px)".concat(" ", "var(--ds-space-050, 4px)"),
  fontWeight: "var(--ds-font-weight-medium, 500)"
});
var textStyles = css({
  padding: "var(--ds-space-0, 0px)".concat(" ", "var(--ds-space-050, 4px)"),
  color: "var(".concat(VAR_SECONDARY_TEXT_COLOR, ")"),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
var rootStyles = css({
  display: 'inline-block',
  maxWidth: '100%',
  '&:focus': {
    outline: '1px solid'
  },
  '&:hover': {
    // eslint-disable-next-line @repo/internal/styles/no-nested-styles
    '[data-ds--inline-message--icon]': {
      // Legacy style
      color: 'var(--icon-accent-color)'
    },
    // eslint-disable-next-line @repo/internal/styles/no-nested-styles
    '[data-ds--inline-message--button]': {
      textDecoration: 'underline'
    }
  },
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  '[data-ds--inline-message--button]': {
    '&:active [data-ds--inline-message--secondary-text]': {
      color: "var(--ds-text-subtle, ".concat("var(".concat(VAR_SECONDARY_TEXT_COLOR, ")"), ")")
    }
  }
});
var titleColor = themed({
  light: "var(--ds-text, ".concat(colors.N600, ")"),
  dark: "var(--ds-text, ".concat(colors.DN600, ")")
});
var textColor = themed({
  light: "var(--ds-text-subtlest, ".concat(colors.N300, ")"),
  dark: "var(--ds-text-subtlest, ".concat(colors.DN100, ")")
});
var iconColor = themed('appearance', {
  connectivity: {
    light: "var(--ds-icon-brand, ".concat(colors.B300, ")"),
    dark: "var(--ds-icon-brand, ".concat(colors.B75, ")")
  },
  confirmation: {
    light: "var(--ds-icon-success, ".concat(colors.G200, ")"),
    dark: "var(--ds-icon-success, ".concat(colors.G200, ")")
  },
  info: {
    light: "var(--ds-icon-discovery, ".concat(colors.P200, ")"),
    dark: "var(--ds-icon-discovery, ".concat(colors.P200, ")")
  },
  warning: {
    light: "var(--ds-icon-warning, ".concat(colors.Y200, ")"),
    dark: "var(--ds-icon-warning, ".concat(colors.Y200, ")")
  },
  error: {
    light: "var(--ds-icon-danger, ".concat(colors.R300, ")"),
    dark: "var(--ds-icon-danger, ".concat(colors.R300, ")")
  }
});

/**
 * __Inline message__
 *
 * An inline message lets users know when important information is available or when an action is required.
 *
 * - [Examples](https://atlassian.design/components/inline-message/examples)
 * - [Code](https://atlassian.design/components/inline-message/code)
 * - [Usage](https://atlassian.design/components/inline-message/usage)
 *
 * @example
 *
 * ```jsx
 * const Component = () => (
 *   <InlineMessage
 *    title="Inline Message Title Example"
 *    secondaryText="Secondary Text"
 *   >
 *    <p>Some text that would be inside the open dialog and otherwise hidden.</p>
 *   </InlineMessage>
 * );
 * ```
 */
var InlineMessage = function InlineMessage(_ref) {
  var _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? 'bottom-start' : _ref$placement,
    _ref$secondaryText = _ref.secondaryText,
    secondaryText = _ref$secondaryText === void 0 ? '' : _ref$secondaryText,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? '' : _ref$title,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'connectivity' : _ref$type,
    appearance = _ref.appearance,
    children = _ref.children,
    testId = _ref.testId,
    iconLabel = _ref.iconLabel;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var toggleDialog = useCallback(function () {
    setIsOpen(function (oldState) {
      return !oldState;
    });
  }, [setIsOpen]);
  var onCloseDialog = useCallback(function () {
    return setIsOpen(false);
  }, [setIsOpen]);
  var theme = useGlobalTheme();
  if (!appearance) {
    appearance = type;
  }
  return jsx("div", {
    css: rootStyles,
    style: {
      '--icon-accent-color': iconColor({
        appearance: appearance,
        theme: theme
      })
    },
    "data-testid": testId
  }, jsx(InlineDialog, {
    onClose: onCloseDialog,
    content: children,
    isOpen: isOpen,
    placement: placement,
    testId: testId && "".concat(testId, "--inline-dialog")
  }, jsx(Button, {
    "data-ds--inline-message--button": true,
    appearance: "subtle-link",
    onClick: toggleDialog,
    spacing: "none",
    testId: testId && "".concat(testId, "--button")
  }, jsx("div", {
    css: buttonContentsStyles
  }, jsx(MessageIcon, {
    isOpen: isOpen,
    appearance: appearance,
    label: iconLabel
  }), title && jsx("span", {
    style: {
      color: titleColor({
        theme: theme
      })
    },
    css: titleStyles,
    "data-testid": testId && "".concat(testId, "--title")
  }, title), secondaryText && jsx("span", {
    "data-ds--inline-message--secondary-text": true,
    style: _defineProperty({}, VAR_SECONDARY_TEXT_COLOR, textColor({
      theme: theme
    })),
    css: textStyles,
    "data-testid": testId && "".concat(testId, "--text")
  }, secondaryText)))));
};
export default InlineMessage;